import React, { ChangeEvent, FormEvent, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ButtonLoading, hasErrors } from "go-form";
import { FormErrorMessage } from "go-form/components/FormErrorMessage";
import { FormErrorMessageAlertApi } from "go-form/components/FormErrorMessageAlert";
import { useAuth } from "../../../services/Security/auth";
import { getApiURL } from "../../../utils/envutil";
import PasswordReminderModal from "../PasswordReminderModal/PasswordReminderModal";

interface State {
	email: string;
	password: string;
	showPassword: boolean;
}

interface Props {
	location: Record<string, any>;
	email?: string;
}

const LoginForm = (props: Props) => {
	const { t } = useTranslation();
	const auth = useAuth();
	const { errors, loading } = auth;
	const [values, setValues] = useState<State>({
		email: props.email ?? "",
		password: "",
		showPassword: false,
	});

	const handleChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
		setValues({ ...values, [prop]: event.target.value });
	};

	const shouldRedirect = () => {
		return !!props.location?.state?.from;
	};

	const getRedirectUrl = () => {
		return `${props.location?.state?.from?.pathname ? props.location.state.from.pathname : ""}${
			props.location?.state?.from?.search ? props.location.state.from.search : ""
		}`;
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (shouldRedirect()) {
			await auth.login(values.email, values.password, rememberMe, true, getRedirectUrl());
		} else {
			await auth.login(values.email, values.password, rememberMe);
		}
	};

	const [rememberMe, setRememberMe] = useState(false);

	const loginWithGoogle = () => {
		const url = getApiURL();
		let redirectUrl: string;
		if (shouldRedirect()) {
			redirectUrl = getApiURL() + getRedirectUrl();
		} else {
			redirectUrl = `${getApiURL()}/dashboard`;
		}
		window.location.href = `${url}/oauth2/authorization/google?${
			props.email ? `login_hint=${props.email}&` : ""
		}redirect_uri=${redirectUrl}`;
	};

	return (
		<>
			<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
				<FormErrorMessageAlertApi errors={errors} />
				<div className="oauth-google" onClick={() => loginWithGoogle()}>
					<div className="oauth-google-inner">
						<img
							src="/graphics/login/google-logo.png"
							className="oauth-google-img"
							style={{ width: "18px", margin: 0, height: "18px" }}
						/>
						<div className="oauth-google-text">
							{t("modules.security.field.continue_with_google.title")}
						</div>
					</div>
				</div>
				<div className="horizontal-row">
					<div className="horizontal-text">{t("common.word.or", { ns: "lib" })}</div>
				</div>
				<Form.Group className="form-group">
					<Form.Control
						disabled={loading || !!props.email}
						id="email"
						isInvalid={hasErrors(errors, "email")}
						onChange={handleChange("email")}
						type="email"
						value={values.email}
						placeholder={t("lib:common.word.email")}
					/>
					<Form.Label htmlFor="email" sm={3}>
						{t("lib:common.word.email")}
					</Form.Label>
					<FormErrorMessage errors={errors} name="email" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Control
						disabled={loading}
						id="password"
						isInvalid={hasErrors(errors, "password")}
						onChange={handleChange("password")}
						type={values.showPassword ? "text" : "password"}
						value={values.password}
						placeholder={t("modules.security.field.password.title")}
					/>
					<Form.Label htmlFor="password" sm={3}>
						{t("modules.security.field.password.title")}
					</Form.Label>
					<FormErrorMessage errors={errors} name="password" />
				</Form.Group>
				<Form.Group
					className="form-group form-check"
					onChange={(e: any) => {
						setRememberMe(e.target.checked);
					}}
				>
					<Form.Check.Input type="checkbox" className="form-check-input" id="remember_me" />
					<Form.Check.Label className="form-check-label" htmlFor="remember_me">
						{t("modules.security.field.remember_me.title")}
					</Form.Check.Label>
					<div className="login-lost-password">
						<Link to="/lost-password">{t("modules.security.action.lost_password.title")}</Link>
					</div>
				</Form.Group>
				<ButtonLoading loading={loading} type="submit">
					{t("modules.security.action.login.title")}
				</ButtonLoading>
			</Form>
			{auth.showReminderPasswordModal && (
				<PasswordReminderModal
					show={auth.showReminderPasswordModal}
					initialEmail={values.email}
					onHide={() => auth.setShowReminderPasswordModal(false)}
				/>
			)}
		</>
	);
};

export default LoginForm;
